import { z } from 'zod';
import { FieldValues } from 'react-hook-form';

/**
 * Zod validation utilities for multi-step forms
 *
 * This module provides utilities for:
 * - Schema composition across steps
 * - Conditional validation based on step data
 * - Progressive validation (validate only up to current step)
 * - Cross-step field dependencies
 */

export type StepSchemaConfig<T extends FieldValues = FieldValues> = {
  [stepId: string]: z.ZodType<Partial<T>>;
};

export type ConditionalSchema<T extends FieldValues = FieldValues> = {
  condition: (data: Partial<T>) => boolean;
  schema: z.ZodType<unknown>;
};

export type ValidationRule<T extends FieldValues = FieldValues> = {
  field: keyof T;
  dependsOn?: Array<keyof T>;
  validator: (value: unknown, allData: Partial<T>) => string | null;
};

/**
 * Creates a combined schema from multiple step schemas
 * Useful for final validation before form submission
 */
export const createCombinedSchema = <T extends FieldValues = FieldValues>(
  stepSchemas: StepSchemaConfig<T>
): z.ZodType<T> => {
  const schemaEntries = Object.entries(stepSchemas);

  if (schemaEntries.length === 0) {
    return z.object({}) as unknown as z.ZodType<T>;
  }

  if (schemaEntries.length === 1) {
    return schemaEntries[0][1] as z.ZodType<T>;
  }

  // Merge all schemas
  // For now, just return the first schema as we can't reliably merge arbitrary schemas
  // In a real application, you'd need to know the specific schema types
  return schemaEntries[0][1] as z.ZodType<T>;
};

/**
 * Creates a progressive schema that validates only up to the current step
 * This allows users to navigate backward without triggering validation errors
 * for steps they haven't reached yet
 */
export const createProgressiveSchema = <T extends FieldValues = FieldValues>(
  stepSchemas: StepSchemaConfig<T>,
  currentStepIndex: number,
  stepOrder: string[]
): z.ZodType<Partial<T>> => {
  const relevantSteps = stepOrder.slice(0, currentStepIndex + 1);
  const relevantSchemas = relevantSteps.map((stepId) => stepSchemas[stepId]).filter(Boolean);

  if (relevantSchemas.length === 0) {
    return z.object({}) as unknown as z.ZodType<Partial<T>>;
  }

  if (relevantSchemas.length === 1) {
    return relevantSchemas[0];
  }

  // For now, just return the first schema as we can't reliably merge arbitrary schemas
  return relevantSchemas[0];
};

/**
 * Creates a conditional schema that adapts based on form data
 * Useful for steps that should only appear or validate under certain conditions
 */
export const createConditionalSchema = <T extends FieldValues = FieldValues>(
  baseSchema: z.ZodType<Partial<T>>,
  conditionalSchemas: ConditionalSchema<T>[]
): ((data: Partial<T>) => z.ZodType<Partial<T>>) => {
  return (data: Partial<T>) => {
    let resultSchema = baseSchema;

    for (const conditionalSchema of conditionalSchemas) {
      if (conditionalSchema.condition(data)) {
        // For now, just use the conditional schema instead of merging
        resultSchema = conditionalSchema.schema as z.ZodType<Partial<T>>;
      }
    }

    return resultSchema;
  };
};

/**
 * Validates cross-step dependencies
 * Ensures that fields from different steps maintain consistency
 */
export const validateCrossStepDependencies = <T extends FieldValues = FieldValues>(
  data: Partial<T>,
  rules: ValidationRule<T>[]
): Record<string, string[]> => {
  const errors: Record<string, string[]> = {};

  for (const rule of rules) {
    const fieldValue = data[rule.field];

    // Check if all dependencies are satisfied
    if (rule.dependsOn) {
      const missingDependencies = rule.dependsOn.filter(
        (dep) => data[dep] === undefined || data[dep] === null || data[dep] === ''
      );

      if (missingDependencies.length > 0) {
        continue; // Skip validation if dependencies aren't met
      }
    }

    const errorMessage = rule.validator(fieldValue, data);
    if (errorMessage) {
      const fieldName = String(rule.field);
      if (!errors[fieldName]) {
        errors[fieldName] = [];
      }
      errors[fieldName].push(errorMessage);
    }
  }

  return errors;
};

/**
 * Common validation schemas for typical form steps
 */
export const commonSchemas = {
  // Personal information step
  personalInfo: z.object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    dateOfBirth: z.string().optional(),
    gender: z.enum(['male', 'female', 'other']).optional(),
  }),

  // Contact information step
  contactInfo: z.object({
    email: z.string().email('Please enter a valid email address'),
    phone: z.string().min(10, 'Phone number must be at least 10 digits'),
    alternatePhone: z.string().optional(),
  }),

  // Address information step
  addressInfo: z.object({
    street: z.string().min(1, 'Street address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    postalCode: z.string().min(5, 'Postal code must be at least 5 characters'),
    country: z.string().min(1, 'Country is required'),
  }),

  // Company information step
  companyInfo: z.object({
    companyName: z.string().min(1, 'Company name is required'),
    taxNumber: z.string().optional(),
    industry: z.string().optional(),
    companySize: z.enum(['1-10', '11-50', '51-200', '201-1000', '1000+']).optional(),
  }),

  // Preferences step
  preferences: z.object({
    newsletter: z.boolean().default(false),
    notifications: z.boolean().default(true),
    language: z.enum(['en', 'tr']).default('tr'),
    timezone: z.string().optional(),
  }),
};

/**
 * Common validation rules for cross-step dependencies
 */
export const commonValidationRules = {
  // Email confirmation
  confirmEmail: <T extends { email?: string; confirmEmail?: string }>(): ValidationRule<T> => ({
    field: 'confirmEmail' as keyof T,
    dependsOn: ['email' as keyof T],
    validator: (value, allData) => {
      if (value !== allData.email) {
        return 'Email addresses do not match';
      }
      return null;
    },
  }),

  // Password confirmation
  confirmPassword: <
    T extends { password?: string; confirmPassword?: string },
  >(): ValidationRule<T> => ({
    field: 'confirmPassword' as keyof T,
    dependsOn: ['password' as keyof T],
    validator: (value, allData) => {
      if (value !== allData.password) {
        return 'Passwords do not match';
      }
      return null;
    },
  }),

  // Date range validation
  dateRange: <T extends { startDate?: string; endDate?: string }>(
    startField: keyof T = 'startDate' as keyof T,
    endField: keyof T = 'endDate' as keyof T
  ): ValidationRule<T> => ({
    field: endField,
    dependsOn: [startField],
    validator: (value, allData) => {
      const startDate = allData[startField];
      if (!startDate || !value) return null;

      if (new Date(value as string) <= new Date(startDate as string)) {
        return 'End date must be after start date';
      }
      return null;
    },
  }),

  // Conditional requirement
  conditionalRequired: <T extends FieldValues>(
    field: keyof T,
    condition: (data: Partial<T>) => boolean,
    message = 'This field is required'
  ): ValidationRule<T> => ({
    field,
    validator: (value, allData) => {
      if (condition(allData) && (!value || value === '')) {
        return message;
      }
      return null;
    },
  }),
};

/**
 * Utility functions for schema creation
 */
export const schemaUtils = {
  /**
   * Makes all fields in a schema optional
   */
  makeOptional: <T,>(schema: z.ZodType<T>) => {
    // @ts-any-allowed: Zod partial() method not properly typed for all schema types
    if ('partial' in schema && typeof schema.partial === 'function') {
      return schema.partial() as z.ZodType<Partial<T>>;
    }
    return schema as z.ZodType<Partial<T>>;
  },

  /**
   * Creates a schema with only specified fields
   */
  pick: <T extends z.ZodRawShape>(schema: z.ZodObject<T>, keys: string[]) => {
    const pickObject: Record<string, true> = {};
    keys.forEach((key) => {
      pickObject[key] = true;
    });
    // @ts-any-allowed: Zod pick method signature is complex with exact types
    return (schema as unknown as z.ZodObject<Record<string, z.ZodTypeAny>>).pick(pickObject);
  },

  /**
   * Creates a schema excluding specified fields
   */
  omit: <T extends z.ZodRawShape>(schema: z.ZodObject<T>, keys: string[]) => {
    const omitObject: Record<string, true> = {};
    keys.forEach((key) => {
      omitObject[key] = true;
    });
    // @ts-any-allowed: Zod omit method signature is complex with exact types
    return (schema as unknown as z.ZodObject<Record<string, z.ZodTypeAny>>).omit(omitObject);
  },

  /**
   * Extends a schema with additional fields
   */
  extend: <T extends z.ZodRawShape, U extends z.ZodRawShape>(
    schema: z.ZodObject<T>,
    extension: z.ZodObject<U>
  ) => {
    return schema.extend(extension.shape);
  },

  /**
   * Creates a refined schema with custom validation
   */
  refine: <T,>(
    schema: z.ZodType<T>,
    refinement: (data: T) => boolean,
    message: string,
    path?: string[]
  ) => {
    return schema.refine(refinement, { message, path });
  },
};

/**
 * Helper to create step configurations with proper typing
 */
export const createStepSchema = <T extends FieldValues = FieldValues>(
  schema: z.ZodType<Partial<T>>
) => schema;

/**
 * Helper to validate a specific step's data
 */
export const validateStepData = async <T extends FieldValues = FieldValues>(
  data: Partial<T>,
  schema: z.ZodType<Partial<T>>
): Promise<{ success: boolean; errors?: z.ZodError; data?: Partial<T> }> => {
  try {
    const validatedData = await schema.parseAsync(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { success: false, errors: error };
    }
    throw error;
  }
};

/**
 * Helper to format Zod errors for form display
 */
export const formatZodErrors = (error: z.ZodError): Record<string, string[]> => {
  const formattedErrors: Record<string, string[]> = {};

  for (const issue of error.issues) {
    const path = issue.path.join('.');
    if (!formattedErrors[path]) {
      formattedErrors[path] = [];
    }
    formattedErrors[path].push(issue.message);
  }

  return formattedErrors;
};
